import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Loader2, Save,
  ArrowUp, ArrowDown,
  Shield, Sprout, Cloud, Compass, BookOpen, Wind,
  Sunrise, Flame, Heart, Users, HeartHandshake, Leaf,
  Waves, Mountain, Sparkles, Moon, Sun, Anchor, Feather, Star,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const ICON_MAP = {
  Shield, Sprout, Cloud, Compass, BookOpen, Wind,
  Sunrise, Flame, Heart, Users, HeartHandshake, Leaf,
  Waves, Mountain, Sparkles, Moon, Sun, Anchor, Feather, Star,
};
const ICON_NAMES = Object.keys(ICON_MAP);

const EMPTY = { slug: "", name: "", icon: "Shield", order: 0 };

export default function AreasManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/areas");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const save = async (payload, isNew) => {
    try {
      if (isNew) {
        await api.post("/admin/areas", payload);
        toast.success("Area added");
      } else {
        await api.patch(`/admin/areas/${editing.id}`, payload);
        toast.success("Area updated");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try {
      await api.delete(`/admin/areas/${a.id}`);
      toast.success("Area deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const move = async (index, direction) => {
    const newItems = [...items];
    const swap = index + direction;
    if (swap < 0 || swap >= newItems.length) return;
    [newItems[index], newItems[swap]] = [newItems[swap], newItems[index]];
    setItems(newItems); // optimistic
    try {
      await api.patch("/admin/areas/reorder/batch", {
        ids_in_order: newItems.map((i) => i.id),
      });
    } catch (err) {
      toast.error("Could not reorder");
      refresh();
    }
  };

  return (
    <div className="space-y-4" data-testid="areas-manager">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif text-2xl text-ink">Areas of support</div>
          <div className="text-sm text-ink-soft">
            Add, rename, reorder or remove the support areas shown on your homepage.
          </div>
        </div>
        <button
          onClick={() => setEditing(EMPTY)}
          data-testid="add-area-button"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
        >
          <Plus size={14} /> New area
        </button>
      </div>

      {loading && (
        <div className="py-12 flex items-center justify-center text-ink-muted">
          <Loader2 size={18} className="animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((a, i) => {
            const Icon = ICON_MAP[a.icon] || Sparkles;
            return (
              <div key={a.id} className="bg-cream-soft border border-sand/70 rounded-2xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 h-10 w-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center">
                    <Icon size={18} strokeWidth={1.5} />
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="font-serif text-lg text-ink leading-tight truncate">{a.name}</div>
                    <div className="text-[11px] text-ink-soft lowercase font-mono truncate">{a.slug}</div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex gap-1">
                    {i > 0 && (
                      <button
                        onClick={() => move(i, -1)}
                        className="p-1.5 rounded-md bg-cream border border-sand hover:border-navy"
                        title="Move up"
                      >
                        <ArrowUp size={12} />
                      </button>
                    )}
                    {i < items.length - 1 && (
                      <button
                        onClick={() => move(i, 1)}
                        className="p-1.5 rounded-md bg-cream border border-sand hover:border-navy"
                        title="Move down"
                      >
                        <ArrowDown size={12} />
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditing(a)}
                      className="inline-flex items-center gap-1 rounded-full bg-cream border border-sand px-3 py-1.5 text-xs hover:border-navy hover:text-navy"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                    <button
                      onClick={() => remove(a)}
                      className="inline-flex items-center gap-1 rounded-full bg-cream border border-sand px-3 py-1.5 text-xs hover:border-red-300 hover:text-red-700"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <AreaForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function AreaForm({ initial, onSave, onClose }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(
        {
          slug: form.slug.trim(),
          name: form.name.trim(),
          icon: form.icon,
          order: Number(form.order) || 0,
        },
        isNew
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 md:p-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-cream rounded-[2rem] p-8 max-w-xl w-full shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div className="font-serif text-2xl text-ink">{isNew ? "New area" : "Edit area"}</div>
          <button onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="area-form">
          <Row label="Name" hint="Shown as the headline on the card">
            <input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} />
          </Row>
          <Row label="Slug" hint="URL id (lowercase, dashes only)">
            <input className={inp} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required pattern="[a-z0-9-]+" />
          </Row>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-2 block">Icon</label>
            <div className="grid grid-cols-5 sm:grid-cols-7 gap-2 bg-cream-soft border border-sand/60 rounded-2xl p-3">
              {ICON_NAMES.map((name) => {
                const Icon = ICON_MAP[name];
                const selected = form.icon === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setForm({ ...form, icon: name })}
                    title={name}
                    className={`h-11 w-11 rounded-xl flex items-center justify-center transition-all ${
                      selected
                        ? "bg-navy text-cream shadow-[0_6px_14px_-6px_rgba(14,59,74,0.7)]"
                        : "bg-cream border border-sand text-ink-muted hover:border-navy hover:text-navy"
                    }`}
                  >
                    <Icon size={17} strokeWidth={1.5} />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-ink/20 px-5 py-2.5 text-sm hover:border-navy hover:text-navy">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-medium hover:bg-navy-deep disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full bg-cream-soft border border-sand/80 rounded-xl px-4 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy";
function Row({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-1.5 block">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-ink-soft mt-1">{hint}</div>}
    </div>
  );
}
